import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BadgeCheck, Loader2, Download, PenTool, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function PortalCertificados() {
  const [certificados, setCertificados] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await (supabase as any)
        .from('certificados_instalacion')
        .select('*')
        .or('archivado.is.null,archivado.eq.false')
        .order('created_at', { ascending: false });
      setCertificados((data ?? []) as any[]);
      setLoading(false);
    })();
  }, []);

  function tipoCertLegible(t: string | null): string {
    if (!t) return '';
    const map: Record<string, string> = {
      pci: 'protección contra incendios',
      intrusion: 'sistema de intrusión',
      cctv: 'videovigilancia (CCTV)',
    };
    return map[t] || t.replace(/_/g, ' ');
  }

  function tituloCertificado(c: any): string {
    const tipo = tipoCertLegible(c.tipo);
    const fecha = c.fecha_instalacion || c.created_at;
    const sufijo = fecha ? ' · ' + format(new Date(fecha), 'MM-yyyy') : '';
    return `Certificado de ${tipo}${sufijo}`;
  }

  async function firmar(certificadoId: string) {
    setSigning(certificadoId);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const r = await fetch(`${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/portal-mint-sign-token-certificado`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sess.session?.access_token}`,
          apikey: (import.meta as any).env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ certificado_id: certificadoId }),
      });
      const payload = await r.json();
      if (!r.ok) throw new Error(payload?.error || 'No se pudo abrir la firma');
      window.open(`/firma-certificado/${payload.token}`, '_blank');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSigning(null);
    }
  }

  async function descargar(path: string | null) {
    if (!path) return toast.error('Aún no hay PDF disponible');
    const { data, error } = await supabase.storage
      .from('certificados-instalacion')
      .createSignedUrl(path, 60);
    if (error || !data) return toast.error('No se pudo descargar');
    window.open(data.signedUrl, '_blank');
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Certificados</h1>
        <p className="text-muted-foreground">Tus certificados de instalación</p>
      </div>

      {certificados.length === 0 && (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No tienes certificados todavía.</CardContent></Card>
      )}

      <div className="grid gap-3">
        {certificados.map(c => (
          <Card key={c.id}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BadgeCheck className="h-4 w-4" />
                    <span className="first-letter:uppercase">{tituloCertificado(c)}</span>
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Ref. {c.numero_formateado}</p>
                </div>
                <EstadoBadge estado={c.estado} />
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Fecha de instalación</p>
                  <p>{c.fecha_instalacion ? format(new Date(c.fecha_instalacion), 'd MMM yyyy', { locale: es }) : '—'}</p>
                </div>
                {c.grado_intrusion && (
                  <div>
                    <p className="text-muted-foreground text-xs">Grado UNE-EN 50131</p>
                    <p><Badge variant="secondary">Grado {c.grado_intrusion}</Badge></p>
                  </div>
                )}
              </div>
              {c.firma_cliente && (
                <Badge variant="outline" className="bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Firmado{c.nombre_firmante_cliente ? ` por ${c.nombre_firmante_cliente}` : ''}
                  {c.fecha_firma ? ` · ${format(new Date(c.fecha_firma), "d MMM yyyy", { locale: es })}` : ''}
                </Badge>
              )}
              <div className="flex gap-2 flex-wrap">
                {!c.firma_cliente && c.estado !== 'anulado' && (
                  <Button size="sm" onClick={() => firmar(c.id)} disabled={signing === c.id}>
                    {signing === c.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PenTool className="h-4 w-4 mr-2" />}
                    Firmar ahora
                  </Button>
                )}
                {c.pdf_path && (
                  <Button size="sm" variant="outline" onClick={() => descargar(c.pdf_path)}>
                    <Download className="h-4 w-4 mr-2" /> Descargar PDF
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function EstadoBadge({ estado }: { estado: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    borrador: { label: 'Borrador', variant: 'secondary' },
    pendiente_firma: { label: 'Pendiente firma', variant: 'destructive' },
    emitido: { label: 'Emitido', variant: 'default' },
    anulado: { label: 'Anulado', variant: 'outline' },
  };
  const e = map[estado] || { label: estado, variant: 'secondary' };
  return <Badge variant={e.variant}>{e.label}</Badge>;
}
